import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { SvgToastInfo } from "ui/svgs";
import { Button } from "ui/button";
import { StateNvr, createNvr } from "lib/actions";
import React, { useState, useMemo, useEffect, useRef } from "react";
import OTPInput from "ui/OTPInput/index";
import momentTimezone from "moment-timezone";
import { Select, Tooltip } from "antd";
import useWsSender from "lib/wsSenderHook";
import { showToast } from "ui/components/common";
import { InfoCircleOutlined } from "@ant-design/icons";
import clsx from "clsx";
import { getNvrById, useWSService } from "./service";
import { use } from "echarts";
const enum UpgradeState {
  NoUpgraderequired = 0,
  Upgrading = 1,
  NeedUpgrade = 2,
}
export default function Form({
  orgId,
  siteId,
}: {
  orgId: string;
  siteId: string;
}) {
  interface formDataType {
    [key: string]: FormDataEntryValue;
  }
  const { upgradeEdge } = useWSService();
  const [macAddress, setMacAddress] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [timezone, setTimezone] = useState("");
  const { sendWsRequest } = useWsSender();
  const responseBody: formDataType = {};

  const [responseData, setResponseData] = useState<StateNvr>();
  const [btnSubmitDisabled, setBtnSubmitDisabled] = useState(false);
  const [upgradeState, setUpgradeState] = useState(
    UpgradeState.NoUpgraderequired,
  );
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const objReturn = responseData?.strReturn;
    const needUpgrade =
      objReturn?.nvr?.devicePrerequisite?.prerequisiteList.find(
        (e) => e.type === "needUpgrade",
      );
    const upgrading = objReturn?.nvr?.devicePrerequisite?.prerequisiteList.find(
      (e) => e.type === "upgrading",
    );
    if (upgrading) {
      setUpgradeState(UpgradeState.Upgrading);
    } else if (needUpgrade) {
      setUpgradeState(UpgradeState.NeedUpgrade);
    }
  }, [responseData]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    formData.forEach(
      (value, property: string) => (responseBody[property] = value),
    );

    try {
      setBtnSubmitDisabled(true);
      const response = await createNvr(orgId, siteId, formData);
      setBtnSubmitDisabled(false);
      setResponseData(response);
      setTimezone(response?.strReturn?.nvr?.timezone);
    } catch (error) {
      console.error(error);
    }
  }
  const timeZonesOption = useMemo(() => {
    const timeZonesOption = momentTimezone.tz.names().map((timeZone) => {
      const offset = momentTimezone.tz(timeZone).format("Z");
      return {
        value: timeZone,
        label: timeZone + "(" + offset + ")",
      };
    });
    return timeZonesOption;
  }, []);
  function submitTimezone() {
    sendWsRequest(
      "device.update_nvr_timezone",
      {
        device_update_nvr_timezone: {
          id: responseData?.strReturn?.nvr?.id,
          timezone,
        },
      },
      { orgId: responseData?.strReturn?.nvr?.orgId },
    )
      .then(() => {
        showToast(0, "Timezone updated successfully");
      })
      .catch(() => {
        showToast(1, "Failed to update timezone");
      });
  }

  const handleUpgrade = async () => {
    setUpgradeState(UpgradeState.Upgrading);
    const nvrId = responseData?.strReturn?.nvr?.id;
    try {
      await upgradeEdge(orgId, nvrId);

      timerIdRef.current = setInterval(() => {
        getNvrById(nvrId, orgId).then((data) => {
          const nvr = data.data.data;
          if (!["upgrading", "initial"].includes(nvr.upgrade.upgradingState)) {
            if (nvr.upgrade.upgradingState === "success") {
              showToast(0, "Upgrade result:" + nvr.upgrade.upgradingState);
              setUpgradeState(UpgradeState.NoUpgraderequired);
            } else {
              showToast(1, "Upgrade result:" + nvr.upgrade.upgradingState);
              setUpgradeState(UpgradeState.NeedUpgrade);
            }
            clearInterval(timerIdRef.current);
          }
        });
      }, 5000);
    } catch (e) {
      setUpgradeState(UpgradeState.NeedUpgrade);
      showToast(
        1,
        "There was a problem during the upgrade. Please try again later.",
      );
    }
  };

  const hasPrerequisiteError = useMemo(() => {
    const objReturn = responseData?.strReturn;
    return objReturn?.nvr?.devicePrerequisite?.prerequisiteList
      .filter((e) => e.type !== "needUpgrade" && e.type !== "upgrading")
      ?.some((e) => e.level === "error");
  }, [responseData]);

  if (responseData?.strReturn) {
    const objReturn = responseData?.strReturn;
    // Filter out prerequisites that are not "needUpgrade" or "upgrading"
    const filtered =
      objReturn?.nvr?.devicePrerequisite?.prerequisiteList.filter(
        (e) => e.type !== "needUpgrade" && e.type !== "upgrading",
      );
    return (
      <div className="max-w-lg mx-auto">
        <div className="p-4 md:p-6 ">
          <h2 className="text-lg font-medium mb-5">Add NVR to site</h2>
          {upgradeState === UpgradeState.Upgrading && (
            <div className="flex items-center gap-2 bg-[#FFEDEB] px-6 py-4 mb-8 rounded-2xl font-semibold text-[#f73d2f]">
              <SvgToastInfo width="15" className="fill-red-500 flex-shrink-0" />
              <div className="flex-grow flex items-center gap-2">
                <p className="text-sm text-red-500 flex-grow">
                  NVR is upgrading, please wait...
                </p>
                <div
                  className="text-blue-500 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              </div>
            </div>
          )}
          {upgradeState === UpgradeState.NeedUpgrade && (
            <div className="flex justify-between gap-2 bg-[#FFEDEB] px-6 py-4 mb-8 rounded-2xl font-semibold text-[#f73d2f]">
              <SvgToastInfo width="15" className="fill-red-500 flex-shrink-0" />
              <p>
                Your NVR is not up to date. Please upgrade to the latest version
                to continue.
              </p>
              <button
                className="bg-[F73D2F] text-white px-6 py-4 bg-red-500 hover:bg-red-400 rounded-xl"
                onClick={handleUpgrade}
              >
                Upgrade
              </button>
            </div>
          )}
          {filtered?.map((e, i) => (
            <div
              className="flex justify-between gap-2 bg-[#FFEDEB] px-6 py-4 mb-8 rounded-2xl font-semibold text-[#f73d2f]"
              key={i}
            >
              <SvgToastInfo width="15" className="fill-red-500 flex-shrink-0" />
              <p>{e.msg}</p>
            </div>
          ))}

          <div className="bg-gray-100 rounded-2xl p-4 md:p-6 grid grid-cols-2 gap-4">
            <div>
              NVR Found
              <CheckCircleIcon className="inline-block ml-3 w-5 md:w-6 text-green-600" />
            </div>
            <div />
            <p className="text-slate-500">NVR Name</p>
            <p>{objReturn?.nvr.name}</p>
            <p className="text-slate-500">Model</p>
            <p>{objReturn?.nvr.model}</p>
            <p className="text-slate-500">MAC Address</p>
            <p>{objReturn?.nvr.macAddress}</p>
            <p className="text-slate-500">Serial Number</p>
            <p>{objReturn?.nvr.sn}</p>
            <p className="text-slate-500" style={{ alignContent: "center" }}>
              Time Zone &nbsp;
              <Tooltip
                placement="top"
                title={
                  "This timezone setting will override the timezone setting in NVR."
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </p>
            <Select
              showSearch
              placeholder="Select a time zone"
              className="h-[54px] rounded-2xl"
              value={timezone}
              onChange={(value) => {
                setTimezone(value);
              }}
              filterOption={(input, option) =>
                ((option?.label ?? "") as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {timeZonesOption?.map((timeZone) => (
                <Select.Option value={timeZone.value} key={timeZone.value}>
                  {timeZone.label}
                </Select.Option>
              ))}
            </Select>
            <br />
            <Button onClick={submitTimezone}>Confirm Timezone</Button>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/dashboard/customers"
            className="flex items-center rounded-2xl bg-gray-100  px-6 py-4  text-base font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancel
          </Link>
          <Link
            to={`/dashboard/customers/${orgId}/sites/${siteId}/nvrs/${objReturn?.nvr.id}/cameras/create`}
            onClick={(e) => {
              if (
                hasPrerequisiteError ||
                upgradeState !== UpgradeState.NoUpgraderequired
              )
                e.preventDefault();
            }}
            className={clsx(
              "flex items-center rounded-2xl px-6 py-4 text-base font-medium text-white transition-colors",
              hasPrerequisiteError ||
                upgradeState !== UpgradeState.NoUpgraderequired
                ? "cursor-not-allowed opacity-50 bg-gray-300 text-zinc-500"
                : "cursor-pointer bg-blue-500 hover:bg-blue-600 text-white",
            )}
          >
            Search Cameras
          </Link>
        </div>
      </div>
    );
  } else {
    return (
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
        <div className="rounded-md">
          <h2 className="text-lg font-medium mb-5">Add NVR to site</h2>

          {/* Site Name */}
          <div className="mb-4">
            <label htmlFor="macAddress" className="mb-2 block text-sm">
              Last 6 digits of MAC Address
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <OTPInput
                  autoFocus
                  length={6}
                  className="otpContainer"
                  inputClassName="otpInput w-12 h-12 mr-3 text-base text-center rounded-md border border-gray-200"
                  isMacAddressInput
                  onChangeOTP={(otp) =>
                    setMacAddress(otp.replace(/(..)/g, "$1:").slice(0, -1))
                  }
                />
                <input
                  id="macAddress"
                  name="macAddress"
                  type="hidden"
                  placeholder="Last 6 digits of MAC Address"
                  className="block w-full rounded-md border border-gray-200 py-2 text-sm outline-2 placeholder:text-gray-500"
                  aria-describedby="macAddress-error"
                  value={macAddress}
                />
              </div>
              <div id="macAddress-error" aria-live="polite" aria-atomic="true">
                {responseData?.errors?.macAddress &&
                  responseData.errors.macAddress.map((error: string) => (
                    <p className="mt-2 text-sm text-red-500" key={error}>
                      {error}
                    </p>
                  ))}
              </div>
            </div>
          </div>

          {/* Serial Number */}
          <div className="mb-4">
            <label htmlFor="serialNumber" className="mb-2 block text-sm">
              Last 6 characters of Serial Number
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <OTPInput
                  autoFocus
                  length={6}
                  className="otpContainer"
                  inputClassName="otpInput w-12 h-12 mr-3 text-base text-center rounded-md border border-gray-200"
                  onChangeOTP={(otp) => setSerialNumber(otp)}
                />
                <input
                  id="serialNumber"
                  name="serialNumber"
                  type="hidden"
                  placeholder="Last 6 characters of Serial Number"
                  className="block w-full rounded-md border border-gray-200 py-2 text-sm outline-2 placeholder:text-gray-500"
                  aria-describedby="serialNumber-error"
                  value={serialNumber}
                />
              </div>
              <div
                id="serialNumber-error"
                aria-live="polite"
                aria-atomic="true"
              >
                {responseData?.errors?.serialNumber &&
                  responseData.errors.serialNumber.map((error: string) => (
                    <p className="mt-2 text-sm text-red-500" key={error}>
                      {error}
                    </p>
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/dashboard/customers"
            className="p-4 flex items-center rounded-2xl bg-gray-100 px-4 text-base font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancel
          </Link>
          <Button type="submit" aria-disabled={btnSubmitDisabled}>
            Continue
          </Button>
        </div>
        {responseData?.message && (
          <div
            className="p-4 mb-4 mt-12 text-red-800 rounded-2xl bg-red-50 dark:bg-gray-800 dark:text-red-400 flex items-center space-x-1"
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
            <p className="text-sm text-red-500">{responseData.message}</p>
          </div>
        )}
      </form>
    );
  }
}
